// src/components/housekeepingV2/drawers/AddOrEditOutOfOrderDrawer.jsx

import React, { useEffect } from "react";
import {
  Drawer,
  Form,
  Select,
  DatePicker,
  Button,
  Space,
  ConfigProvider,
  message
} from "antd";
import { useDispatch, useSelector } from "react-redux";

import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

import enGB from 'antd/es/locale/en_GB';
import {
  createOOOThunk,
  updateOOOThunk,
} from "../../../reducer/outOfOrderRoomsReducer";

dayjs.locale('en-gb');

const AddOrEditOutOfOrderDrawer = ({
  visible,
  onClose,
  hotelId,
  rooms,
  mode,    // "add" or "edit"
  record,  // existing record when editing
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((s) => s.outOfOrderRooms.loading);
  const [form] = Form.useForm();

  // set moment locale once
  // useEffect(() => {
  //   moment.locale("en-gb");
  // }, []);

  // when drawer opens, either reset or populate
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }
    if (mode === "edit" && record) {
      form.setFieldsValue({
        roomIds:   record.roomIds,
        startDate: dayjs(record.startDate),
        endDate:  dayjs(record.endDate),
      });
    } else if(mode === "add" && record?.roomIds){
      form.setFieldsValue({ roomIds: record.roomIds });
    } else {
      form.resetFields();
    }
  }, [visible, mode, record, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = ({ roomIds, startDate, endDate }) => {
    // clone & startOf prevents any timezone offset errors
    const payload = {
      roomIds,
      startDate: startDate.clone().startOf("day").toDate(),
      endDate:   endDate.clone().endOf("day").toDate(),
      status:    "ROOM_OUT_OF_ORDER",
    };

   const thunkAction =
      mode === "edit"
        ? updateOOOThunk({ id: record.id, ...payload })
        : createOOOThunk({ hotelId, ...payload });

         dispatch(thunkAction)
      .unwrap()
      .then(() => {
        message.success(
          mode === "edit"
            ? "Out-of-order entry updated"
            : "Out-of-order entry created"
        );
        handleClose();
      })
      .catch((err) => {
        console.error('Error saving out-of-order entry:', err);
        message.error(
          err?.message || "Failed to save out-of-order entry"
        );
      });

    handleClose();
  };

  return (
    <ConfigProvider locale={enGB}>
      <Drawer
        title={mode === "edit" ? "Edit Out Of Order Rooms" : "Add Out Of Order Rooms"}
        width={480}
        onClose={handleClose}
        open={visible}
        destroyOnClose
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="roomIds"
            label="Rooms"
            rules={[{ required: true, message: "Please select at least one room" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select rooms"
              optionFilterProp="label" 
              options={rooms.map((r) => ({ label: r.name, value: r.id }))}
              filterOption={(input, option) =>      // 3) optional: case-insensitive substring match
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: "Please pick start date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End Date"
            rules={[{ required: true, message: "Please pick end date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              disabledDate={(current) => {
                // get the startDate value from the form
                const start = form.getFieldValue("startDate");
                // if start exists, disable any day before it
                return start ? current.isBefore(start, "day") : false;
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {mode === "edit" ? "Update" : "Submit"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </ConfigProvider>
  );
};

export default AddOrEditOutOfOrderDrawer;